(() => {
  if (!window.customElements || customElements.get('wf-deferred-account')) return;

  let componentPromise;

  function loadAccountComponent() {
    if (customElements.get('shopify-account')) return Promise.resolve();

    // Shopify can also inject this module. Module caching avoids executing it
    // twice; this import is only a fallback when Account is actually requested.
    componentPromise ??= import('https://cdn.shopify.com/storefront/web-components/account.js')
      .then(() => customElements.whenDefined('shopify-account'))
      .catch((error) => {
        componentPromise = undefined;
        throw error;
      });
    return componentPromise;
  }

  class DeferredAccount extends HTMLElement {
    #controller;
    #observer;
    #timer;
    #attempt = 0;
    #loading = false;
    #link;
    #mountedNodes = [];

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.#link = this.querySelector('.account-button__link');
      if (!this.#link) return;

      const options = { signal: this.#controller.signal };
      this.#link.addEventListener('click', this.#onClick, options);
      window.addEventListener('pagehide', this.#cancelPending, options);
    }

    disconnectedCallback() {
      this.#cancelPending();
      this.#controller?.abort();
    }

    #clearWait() {
      window.clearTimeout(this.#timer);
      this.#observer?.disconnect();
      this.#observer = undefined;
      this.#link?.removeAttribute('aria-busy');
    }

    #cancelPending = () => {
      if (!this.#loading) return;
      this.#attempt++;
      this.#loading = false;
      this.#clearWait();
      this.removeAttribute('data-ready');
      this.#mountedNodes.forEach((node) => node.remove());
      this.#mountedNodes = [];
    };

    #fallback(attempt) {
      if (attempt !== this.#attempt || !this.isConnected || !this.#loading) return;
      const destination = this.#link.href;
      this.#cancelPending();
      window.location.assign(destination);
    }

    #onClick = (event) => {
      // Preserve new-tab, download and browser-native link actions.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!window.HTMLDialogElement || !HTMLDialogElement.prototype.showModal) return;
      if (!this.querySelector('template')) return;

      event.preventDefault();
      if (this.#loading) return;

      this.#loading = true;
      const attempt = ++this.#attempt;
      this.#link.setAttribute('aria-busy', 'true');
      this.#timer = window.setTimeout(() => this.#fallback(attempt), 8000);

      loadAccountComponent()
        .then(() => {
          if (attempt !== this.#attempt || !this.isConnected) return;
          const template = this.querySelector('template');
          const content = template?.content.cloneNode(true);
          const account = content?.querySelector('shopify-account');
          if (!account) throw new Error('Account template is unavailable');

          // Shopify injects its store context beside the account element.
          // Mount the full template so menu requests keep their store and locale.
          this.#mountedNodes = [...content.childNodes];
          account.addEventListener('open', () => {
            if (attempt !== this.#attempt) return;
            this.#loading = false;
            this.#clearWait();
          }, { once: true });
          this.insertBefore(content, this.#link);

          // Let Shopify create and wire its native trigger. Using that trigger
          // retains its menu, focus restoration and signed-in behaviour.
          const openWhenReady = () => {
            if (attempt !== this.#attempt || !this.isConnected) return;
            const trigger = account.shadowRoot?.querySelector('button[aria-haspopup="dialog"], a');
            if (!trigger) return;
            this.#observer?.disconnect();
            this.#observer = undefined;

            // Legacy accounts navigate directly instead of opening a dialog.
            if (trigger.matches('a')) {
              this.#fallback(attempt);
              return;
            }

            // Do not open a delayed dialog after the visitor has moved on.
            if (document.activeElement !== this.#link && document.activeElement !== document.body) {
              this.#cancelPending();
              return;
            }

            this.setAttribute('data-ready', '');
            trigger.focus({ preventScroll: true });
            trigger.click();
          };

          if (account.shadowRoot) {
            this.#observer = new MutationObserver(openWhenReady);
            this.#observer.observe(account.shadowRoot, { childList: true, subtree: true });
          }
          openWhenReady();
        })
        .catch(() => this.#fallback(attempt));
    };
  }

  customElements.define('wf-deferred-account', DeferredAccount);
})();
